import { create, all } from 'mathjs'

const mathjs = create(all)
mathjs.config({ number: 'BigNumber' })

const math = {
  // 加
  add(num1: any, num2: any) {
    if (!num1 || num1 === 'undefined') num1 = 0
    if (!num2 || num2 === 'undefined') num2 = 0
    const rs: any = mathjs.add(mathjs.bignumber(num1), mathjs.bignumber(num2))

    return rs.toString()
  },
  // 乘
  multiply(num1: any, num2: any, fixed: number = 6, ignoreFixed?: boolean) {
    if (!num1 || num1 === 'undefined') num1 = 0
    if (!num2 || num2 === 'undefined') num2 = 0

    const rs: any = mathjs.multiply(mathjs.bignumber(num1), mathjs.bignumber(num2))
    let result = mathjs.format(rs, { notation: 'fixed', precision: fixed })
    if (ignoreFixed) {
      result = rs
    }

    return result.toString()
  },
  // 减
  subtract(num1: any, num2: any) {
    if (!num1 || num1 === 'undefined') num1 = 0
    if (!num2 || num2 === 'undefined') num2 = 0
    const rs: any = mathjs.subtract(mathjs.bignumber(num1), mathjs.bignumber(num2))

    return rs.toString()
  },
  // 除
  divide(num1: any, num2: any) {
    if (!num1 || num1 === 'undefined') num1 = 0
    if (!num2 || num2 === 'undefined') num2 = 0

    const rs: any = mathjs.divide(mathjs.bignumber(num1), mathjs.bignumber(num2))

    return rs.toString()
  },
}

export function formatNumberWithUnit(num: number | string, precision = 0): string {
  // 将字符串类型的 num 转换成数字
  const number = typeof num === 'string' ? parseFloat(num) : num

  const units = ['K', 'M', 'B']
  let unitIndex = -1

  // 确定单位
  for (let i = 0; i < units.length; i++) {
    if (number >= 1000 && number < 1000000) {
      unitIndex = 0
      break
    } else if (number >= 1000000 && number < 1000000000) {
      unitIndex = 1
      break
    } else if (number >= 1000000000) {
      unitIndex = 2
      break
    }
  }

  // 格式化数值
  if (unitIndex !== -1) {
    const formattedNum = mathjs.format(number / Math.pow(1000, unitIndex + 1), {
      notation: 'fixed',
      precision,
    })
    return `${formattedNum} ${units[unitIndex]}`
  } else {
    return mathjs.format(number, { notation: 'fixed', precision })
  }
}

export function formatNumberWithMB(num: number | string, precision = 0): string {
  // 将字符串类型的 num 转换成数字
  const number = typeof num === 'string' ? parseFloat(num) : num
  const units = ['M', 'B']
  let unitIndex = -1

  // 确定单位
  for (let i = 0; i < units.length; i++) {
    if (number >= 1000000 && number < 1000000000) {
      unitIndex = 0
      break
    } else if (number >= 1000000000) {
      unitIndex = 1
      break
    }
  }

  // 格式化数值
  if (unitIndex !== -1) {
    const formattedNum = mathjs.format(Math.floor(number / Math.pow(1000, unitIndex + 2)), {
      notation: 'fixed',
      precision,
    })
    return `${formatNumberWithCommas(formattedNum)} ${units[unitIndex]}`
  } else {
    return formatNumberWithCommas(number)
  }
}

interface FormattedNumberProps {
  num: number | string
  units?: string[]
  precision?: number
  withCommas?: boolean
}

interface FormattedNumberReturn {
  number: number | string
  unit: string | null
}

export function formatNumberWithUnits({
  num,
  units = ['M', 'B'],
  precision = 0,
  withCommas = false,
}: FormattedNumberProps): FormattedNumberReturn {
  // 将字符串类型的 num 转换成数字
  const number = typeof num === 'string' ? parseFloat(num) : num

  let unitIndex = -1

  // 确定单位
  for (let i = 0; i < units.length; i++) {
    if (i === 0 && number >= 1000000 && number < 1000000000) {
      unitIndex = 0
      break
    } else if (i === 1 && number >= 1000000000) {
      unitIndex = 1
      break
    }
  }

  // 格式化数值
  if (unitIndex !== -1) {
    const formattedNum = mathjs.format(Math.floor(number / Math.pow(1000, unitIndex + 2)), {
      notation: 'fixed',
      precision,
    })
    return {
      number: withCommas ? `${formatNumberWithCommas(formattedNum)}` : formattedNum,
      unit: units[unitIndex],
    }
  } else {
    return {
      number: withCommas ? `${formatNumberWithCommas(number)}` : num,
      unit: null,
    }
  }
}

export function formatNumberWithCommas(num: number | string): string {
  // 将字符串类型的 num 转换成数字
  const number = typeof num === 'string' ? parseFloat(num) : num
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(number)
}

export default math
